import React, { FC } from "react";

import { useTranslation } from "react-i18next";
import { useMount } from "react-use";

import { PaginatedList } from "@/components/templates";
import { FeaturePageState, Request, RequestCategory } from "@/shared/domain";
import { useNavigation, useRouterHook } from "@/shared/hooks";
import * as tagManager from "@/tagManager";
import { useUserFeedback } from "@/userFeedback";

import RequestCard from "../components/RequestCard/RequestCard";
import RequestTable from "../components/RequestTable/RequestTable";
import useRequestsQueries from "../useRequestsQueries";

const RequestsPage: FC = () => {
    const {
        state: { filterId },
    } = useRouterHook<FeaturePageState>();
    const { t } = useTranslation();
    const { incUsageCount } = useUserFeedback();
    const { navToRequestsSearch } = useNavigation();

    useMount(() => {
        incUsageCount();
        tagManager.pushViewItemListEvent("request", "Requests");
    });

    const { tableMode, useRequestsPageQuery } = useRequestsQueries();

    const requestCategoriesFilters = [
        {
            id: RequestCategory.ALL,
            label: t("Common.All"),
        },
        {
            id: RequestCategory.PENDING,
            label: t("Common.Pending"),
        },
        {
            id: RequestCategory.IN_PROGRESS,
            label: t("Common.InProgress"),
        },
        {
            id: RequestCategory.PROCESSED,
            label: t("Common.Processed"),
        },
    ];

    const {
        page,
        setPage,
        category,
        isFetching,
        setCategory,
        totalOfPages,
        result: requests,
        handlePageSizeChange,
        orderBy,
        handleSort,
    } = useRequestsPageQuery({ defaultCategory: filterId || RequestCategory.ALL });

    const renderItem = (request: Request) => <RequestCard key={request.id} request={request} />;

    const renderTable = (items: Request[]) => (
        <RequestTable requests={items} orderBy={orderBy} handleSort={handleSort} />
    );

    return (
        <PaginatedList<Request>
            page={page}
            tableMode={tableMode}
            emptyListMessage={t("Requests.Empty")}
            pageTitle={t("Common.MyDemands")}
            renderItem={renderItem}
            renderTable={renderTable}
            isFetchingList={isFetching}
            isLoading={isFetching}
            setPage={setPage}
            category={category}
            totalOfPages={totalOfPages}
            availableFilters={requestCategoriesFilters}
            alwaysShowFilters
            setCategory={setCategory}
            items={requests}
            handlePageSizeChange={handlePageSizeChange}
            openSearch={navToRequestsSearch}
            displayLinkedDocumentsButton={false}
        />
    );
};

export default RequestsPage;
